import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { brand } from "../../config/theme";
import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import CompanySelector from "../../components/base/CompanySelector";
import { EmptyState } from "../../components/ui";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useIsReseller } from "../../hooks/useIsReseller";
import { useAccess } from "../../hooks/useAccess";

import { MENU, RADAR_CORREO_BASE, radarCorreoApp } from "./config";
import { useAlerts } from "./hooks/useRadarCorreo";

// Shell del Radar de Correo: menú de secciones, selector de compañía para
// administradores de plataforma y la sección activa vía <Outlet/>. Sin
// compañía elegida no se llama a la API (respondería 403).
export default function RadarCorreoLayout() {
  const { t } = useTranslation("radarCorreo");
  const { t: tc } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const companyId = useCompaniesStore((state) => state.companyId);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const { isReseller } = useIsReseller();
  const hasCompany = Boolean(companyId || localStorage.getItem("companyId") || localStorage.getItem("userCompanyId"));
  const openAlerts = useAlerts("open", hasCompany);

  useEffect(() => {
    const stored = localStorage.getItem("companyId");
    if (stored && stored !== companyId) setCompanyId(stored);
    fetchAllCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon || app.isLocked) {
      Swal.fire({
        title: app.isComingSoon ? tc("appsSidebar.comingSoon") : tc("appsSidebar.premiumFeature"),
        text: app.isComingSoon ? tc("appsSidebar.comingSoonText", { name: app.name }) : tc("appsSidebar.premiumRequired", { name: app.name }),
        icon: app.isComingSoon ? "info" : "warning",
        confirmButtonText: tc("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      setIsAppsSidebarOpen(false);
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = MENU.filter((item) => canAccessSectionOfApp(radarCorreoApp.id, item.id)).map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${RADAR_CORREO_BASE}/${item.id}`,
    count: item.id === "alertas" && hasCompany ? openAlerts.data?.length : undefined,
    loading: item.id === "alertas" && hasCompany ? openAlerts.isLoading : false,
  }));

  return (
    <div className="flex h-[100dvh] pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="flex items-center justify-center gap-2 bg-warning px-4 py-2 text-center text-sm text-white">
          <i className="ri-alarm-warning-line" />
          <span>{tc("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("appName")}
        subtitle={t("appSubtitle")}
        onOpenAppsSidebar={() => setIsAppsSidebarOpen(true)}
        isAppsSidebarOpen={isAppsSidebarOpen}
        onToggleMobileMenu={() => setIsMobileMenuOpen((p) => !p)}
        isMobileMenuOpen={isMobileMenuOpen}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId ?? null}
        onCompanyChange={(id) => setCompanyId(id ?? null)}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
          onNavigate={(path) => navigate(path)}
          companySelector={
            isReseller ? (
              <CompanySelector
                value={companyId ?? null}
                onChange={(id) => setCompanyId(id ?? null)}
                isClearable
                companies={allCompanies.length > 0 ? allCompanies : undefined}
                className="w-full"
              />
            ) : undefined
          }
          isActiveRoute={(itemId) => location.pathname === `${RADAR_CORREO_BASE}/${itemId}`}
        />
        <div className="flex min-w-0 flex-1 justify-center overflow-hidden">
          {hasCompany ? <Outlet /> : <EmptyState icon="ri-building-line" title={t("common.selectCompany")} className="m-auto" />}
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={() => setIsAppsSidebarOpen(false)}
        categories={filteredCategories}
        selectedAppId={radarCorreoApp.id}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? { first_name: user.first_name, last_name: user.last_name, email: user.email, username: user.username } : undefined}
        onProfileClick={() => {
          setIsAppsSidebarOpen(false);
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
